import { AxiosResponseHeaders } from "axios";
import { saveAs } from "file-saver";
import { FC, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  useExportUserReport,
  useGetManageUserRolesList,
  useGetSearchUserList,
} from "../../api/services/manageUser/manageUserApi";
import { MuiBox } from "../../components";
import { ADMIN_USER_GROUPS, PAGE_IDS } from "../../helpers/constants";
import { UserRole } from "../../helpers/types/common";
import { filterUserRoles, isNotEmpty } from "../../helpers/utils";
import { useSiteCore } from "../../hooks/useSiteCore";
import { useToast } from "../../hooks/useToast";
import { toggleLoader } from "../../redux/slices/appState/appStateSlice";
import { CreateUser } from "../createUser/CreateUser";
import { createUserSiteCoreKeys } from "../createUser/siteCore/SiteCoreKeys";
import { getFileNameFromHeader } from "../reports/helper";
import { ViewUser } from "../viewUser/ViewUser";
import { UsersSearchForm } from "./components/usersSearchForm/UsersSearchForm";
import { UsersSearchHeader } from "./components/usersSearchHeader/UsersSearchHeader";
import { UsersSearchResults } from "./components/usersSearchResults/UsersSearchResults";
import {
  USER_STATUS_TYPE_VALUE,
  UsersSearchFormDefaultValues,
  UsersSearchFormType,
} from "./modelsAndSchemas/models";

export const pageId = PAGE_IDS.SEARCH_USERS;

export const UsersSearch: FC = () => {
  const [isSearchClick, setIsSearchClick] = useState(false);
  const [userModalState, setUserModalState] = useState({
    showAdd: false,
    showView: false,
    selectedId: "",
    showEdit: false,
  });
  const { displayToast } = useToast();

  const { commonLabels } = useSiteCore();
  const dispatch = useDispatch();
  const [userRolesList, setUserRolesList] = useState<UserRole[]>([]);

  const methods = useForm<UsersSearchFormType>({
    mode: "all",
    defaultValues: UsersSearchFormDefaultValues,
    // resolver: yupResolver(SearchMdmActivitySchema(commonValidationMessages)),
  });

  const {
    data: manageUserRolesList,
    isPending: isManageUserRolesListLoading,
    isError: isManageUserRolesListError,
    isSuccess: isManageUserRolesListSuccess,
  } = useGetManageUserRolesList();

  const {
    isPending: isSearchPending,
    data: searchResult,
    isError: isSearchUsersError,
    mutateAsync: refetchSearchResults,
  } = useGetSearchUserList();

  const {
    isPending: isExportUserPending,
    mutateAsync: exportUserReport,
    isError: isExportUserError,
  } = useExportUserReport();

  const { reset, getValues } = methods;

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isSearchPending ||
          isManageUserRolesListLoading ||
          isExportUserPending,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isSearchPending, isManageUserRolesListLoading, isExportUserPending]);

  useEffect(() => {
    if (
      isSearchUsersError ||
      isManageUserRolesListError ||
      isExportUserPending
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isSearchUsersError, isManageUserRolesListError, isExportUserError]);

  useEffect(() => {
    if (isManageUserRolesListSuccess && isNotEmpty(manageUserRolesList)) {
      const filteredRoles = filterUserRoles(manageUserRolesList, [
        ADMIN_USER_GROUPS.G3,
        ADMIN_USER_GROUPS.G4,
      ]);
      setUserRolesList(filteredRoles);
    }
  }, [isManageUserRolesListSuccess, manageUserRolesList]);

  const onAddUser = () => {
    setUserModalState({
      showAdd: true,
      showView: false,
      selectedId: "",
      showEdit: false,
    });
  };

  const handleSearchClick = async () => {
    setIsSearchClick(true);
    getSearchResults();
  };

  const handleClearAllSearch = () => {
    reset(UsersSearchFormDefaultValues);
    setIsSearchClick(false);
    getSearchResults();
  };

  const onUserSelect = (id: string) => {
    setUserModalState({
      showAdd: false,
      showView: true,
      selectedId: id,
      showEdit: false,
    });
  };

  const handleEditUser = (id: string) => {
    setUserModalState({
      showAdd: false,
      showView: false,
      selectedId: id,
      showEdit: true,
    });
  };

  useEffect(() => {
    getSearchResults();
  }, []);

  const getSearchResults = () => {
    const {
      userName,
      userNameFilter,
      userEmail,
      userEmailFilter,
      serviceOffice,
      serviceOfficeFilter,
      userStatus,
      role,
    } = getValues();

    let userStatusValue = "";

    if (isNotEmpty(userStatus)) {
      userStatusValue =
        userStatus === createUserSiteCoreKeys.statusActive
          ? USER_STATUS_TYPE_VALUE.ACTIVE
          : USER_STATUS_TYPE_VALUE.INACTIVE;
    }

    const params = {
      userName,
      userNameFilter,
      userEmailAddress: userEmail,
      userEmailAddressFilter: userEmailFilter,
      serviceOfficeName: serviceOffice,
      serviceOfficeNameFilter: serviceOfficeFilter,
      userStatus: userStatusValue,
      roleId: role,
    };
    refetchSearchResults(params);
  };

  const handleCloseAddUserModal = () => {
    setUserModalState({
      showAdd: false,
      showView: false,
      selectedId: "",
      showEdit: false,
    });
  };

  const handleSuccessCreateUser = () => {
    handleCloseAddUserModal();
    getSearchResults();
  };
  const getUserStatusBoolean = (status: string): boolean | undefined => {
    if (status === createUserSiteCoreKeys.statusActive) return true;
    if (status === createUserSiteCoreKeys.statusInActive) return false;
    return undefined;
  };
  const handleExport = async () => {
    const {
      userName,
      userNameFilter,
      userEmail,
      userEmailFilter,
      serviceOffice,
      serviceOfficeFilter,
      userStatus,
      role,
    } = getValues();
    const params = {
      userName,
      userNameFilter,
      userEmailAddress: userEmail,
      userEmailAddressFilter: userEmailFilter,
      serviceOfficeName: serviceOffice,
      serviceOfficeNameFilter: serviceOfficeFilter,
      userStatus: getUserStatusBoolean(userStatus),
      roleId: role,
    };
    const response = await exportUserReport(params);
    const filename = getFileNameFromHeader(
      response.headers as AxiosResponseHeaders,
    );
    saveAs(response.data, filename);
  };

  return (
    <MuiBox data-testid="users-search">
      <UsersSearchHeader pageId={pageId} onAddUser={onAddUser} />

      <FormProvider {...methods}>
        <UsersSearchForm
          pageId={pageId}
          handleSearchClick={handleSearchClick}
          handleClearAllSearch={handleClearAllSearch}
          disableActionBtn={false}
          rolesList={userRolesList}
        />
      </FormProvider>

      <UsersSearchResults
        pageId={pageId}
        rows={searchResult ?? []}
        isSearchClick={isSearchClick}
        onUserSelect={onUserSelect}
        handleEditUser={handleEditUser}
        onHandleExport={handleExport}
      />

      {(userModalState.showAdd || userModalState.showEdit) && (
        <CreateUser
          open={userModalState.showAdd || userModalState.showEdit}
          onClose={handleCloseAddUserModal}
          handleSuccess={handleSuccessCreateUser}
          isEditUser={userModalState.showEdit}
          selectedUserId={userModalState.selectedId}
        />
      )}
      {userModalState.showView && (
        <ViewUser
          open={userModalState.showView}
          onClose={handleCloseAddUserModal}
          staffId={userModalState.selectedId}
          handleEditUser={handleEditUser}
        />
      )}
    </MuiBox>
  );
};
